import { useState, useEffect, useCallback } from 'react';
import { getAllPlatforms, type ConnectedPlatform } from '@/services/data/socialConnections';

const REQUIRED_NETWORKS = ['instagram', 'tiktok', 'youtube', 'linkedin', 'facebook'] as const;

interface NetworkConnectionsStatus {
  allNetworksConnected: boolean;
  connectedNetworks: string[];
  disconnectedNetworks: string[];
  connections: ConnectedPlatform[];
  isLoading: boolean;
  refetch: () => void;
}

export function useNetworkConnectionsStatus(userId: string | undefined): NetworkConnectionsStatus {
  const [connections, setConnections] = useState<ConnectedPlatform[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const checkConnections = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await getAllPlatforms(userId);
      
      if (result.success && result.data) {
        setConnections(result.data);
      } else {
        setConnections([]);
      }
    } catch (error) {
      console.error('Error checking network connections:', error);
      setConnections([]);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    checkConnections();
  }, [checkConnections]);

  const refetch = useCallback(() => {
    checkConnections();
  }, [checkConnections]);

  // Calculate connected/disconnected networks
  const connectedNetworks = connections
    .filter(c => c.connected)
    .map(c => c.platform.toLowerCase());
  
  const disconnectedNetworks = REQUIRED_NETWORKS.filter(
    network => !connectedNetworks.includes(network)
  );

  const allNetworksConnected = disconnectedNetworks.length === 0;

  return {
    allNetworksConnected,
    connectedNetworks,
    disconnectedNetworks: disconnectedNetworks as string[],
    connections,
    isLoading,
    refetch,
  };
}
